import './index.css'
import cn from 'clsx'

import React, { Component } from 'react'
import { LuCloudUpload } from '@react-icons/all-files/lu/LuCloudUpload'
import { LuRefreshCw } from '@react-icons/all-files/lu/LuRefreshCw'
import { LuTrash2 } from '@react-icons/all-files/lu/LuTrash2'
import FileSelect from 'components/common/file-select'
import FileDropWrapper from 'containers/FileDropWrapper'

const DEFAULT_BLANK_MESSAGE = 'Drag and drop artwork'

/**
 * Editable image component with drag-and-drop functionality
 *
 * Filled state shows the artwork with replace and remove actions revealed on
 * hover or keyboard focus. Empty state invites a drop, with a browse fallback.
 * Clicking the tile opens the file picker only while empty; once filled, use
 * the explicit replace action so a stray click can't reopen the picker.
 */
export default class EditableImage extends Component<{
  image?: string | File
  onImageSet: (...args: Array<any>) => any
  blankStateMessage?: string
  /** Secondary line under the empty state, e.g. accepted formats. */
  blankStateHint?: string
  helpText?: React.ReactNode
  disableEditing?: boolean
}> {
  _fileSelect = React.createRef<FileSelect>()

  _openPicker = (e?: React.SyntheticEvent) => {
    e?.stopPropagation()
    this._fileSelect.current?.openFilesDialog()
  }

  _onFileDrop: (files: Array<File>) => void = (files: File[]) => {
    // handle images only
    if (files[0]!.type.indexOf('image/') !== 0) return

    this._processImageFiles(files)
  }
  _processImageFiles: (files: Array<File>, triggerUpdate?: boolean) => void = (
    files: File[],
    triggerUpdate: boolean = true,
  ) => {
    // show preview without triggering server upload
    const reader = new FileReader()

    reader.onload = e => {
      if (e.target?.result) {
        this.props.onImageSet(e.target.result, files[0]!, triggerUpdate)
      }
    }

    reader.readAsDataURL(files[0]!)
  }
  _onRemove: (e: React.SyntheticEvent<EventTarget>) => void = (
    e: React.SyntheticEvent<any>,
  ) => {
    e.stopPropagation()
    this.props.onImageSet(null, null)
  }

  _renderFilled(src: string | undefined): React.ReactNode {
    return (
      <div className="EditableImage_Filled">
        {src && (
          <img className="EditableImage_Img" src={src} alt="Track artwork" />
        )}
        {!this.props.disableEditing && (
          <div className="EditableImage_Overlay">
            <button
              type="button"
              className="EditableImage_Action"
              aria-label="Replace artwork"
              onClick={this._openPicker}
            >
              <LuRefreshCw size={17} />
            </button>
            <button
              type="button"
              className="EditableImage_Action"
              aria-label="Remove artwork"
              onClick={this._onRemove}
            >
              <LuTrash2 size={17} />
            </button>
          </div>
        )}
      </div>
    )
  }

  _renderEmpty(): React.ReactNode {
    if (this.props.disableEditing) {
      return <div className="EditableImage_Empty">No image</div>
    }
    return (
      <div className="EditableImage_Empty">
        <LuCloudUpload className="EditableImage_EmptyIcon" size={26} />
        <div className="EditableImage_EmptyPrimary">
          {this.props.blankStateMessage || DEFAULT_BLANK_MESSAGE}
        </div>
        <button
          type="button"
          className="EditableImage_BrowseLink"
          onClick={this._openPicker}
        >
          or browse files
        </button>
        {this.props.blankStateHint && (
          <div className="EditableImage_EmptyHint">
            {this.props.blankStateHint}
          </div>
        )}
      </div>
    )
  }

  render(): React.ReactElement<'div'> {
    const { disableEditing, image } = this.props
    const src = typeof image === 'string' ? image : undefined
    const body = image ? this._renderFilled(src) : this._renderEmpty()

    if (disableEditing) {
      return (
        <div className={cn('EditableImage', 'is-editing-disabled')}>
          <div className="EditableImage_Box">{body}</div>
        </div>
      )
    }

    return (
      <div className="EditableImage">
        <FileDropWrapper onDrop={this._onFileDrop}>
          <FileSelect
            ref={this._fileSelect}
            className="EditableImage_Box"
            // Once filled, only the explicit replace action opens the picker.
            openOnClick={!image}
            onChange={this._onFileDrop}
          >
            {body}
          </FileSelect>
        </FileDropWrapper>
        {this.props.helpText}
      </div>
    )
  }
}
